import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ALL_CLIENTS_FLAG,
  clearActiveClientContext,
  markClientContextReady,
  resetClientContextReady,
  setActiveClientContext,
  storageKeyFor,
} from "@/lib/clientContext";

/**
 * ClientContext — FILTRO GLOBAL DE CLIENTES (fonte única do frontend).
 *
 * Separação canônica:
 *
 *   ESCOPO AUTORIZADO      `clients` (GET /clients, já filtrado pelo backend
 *                          conforme os vínculos do usuário);
 *   PERMISSÃO OPERACIONAL  decidida pelo Authorization Engine (aqui não se
 *                          concede nem revoga nada);
 *   FILTRO GLOBAL          `selectedClientIds` — o que o usuário QUER ver
 *                          agora, DENTRO do escopo autorizado.
 *
 * Persistência (dupla, com o BANCO como fonte de verdade):
 *   1. `users.client_context` no backend (PUT /me/client-context) — vale
 *      entre sessões, navegadores e abas;
 *   2. cache local ISOLADO POR USUÁRIO (`astral:selected-clients:<id>`),
 *      para a primeira pintura sem esperar a rede. Uma conta NUNCA herda a
 *      preferência de outra.
 *
 * O contexto é consumido por TODOS os módulos pelo interceptor do axios
 * (`@/lib/api`), que envia `X-Astral-Client-Context` em cada requisição.
 * O backend revalida a seleção contra o escopo efetivo.
 */

const ClientContext = createContext(null);

export const ALL_CLIENTS = ALL_CLIENTS_FLAG;

const CONTEXT_EVENT = "astral:client-context-changed";

function readLocal(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed === ALL_CLIENTS_FLAG) return ALL_CLIENTS_FLAG;
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === "string") return [parsed];
  } catch {
    /* preferência corrompida: ignora */
  }
  return null;
}

function writeLocal(userId, value) {
  if (!userId) return;
  try {
    localStorage.setItem(storageKeyFor(userId), JSON.stringify(value));
  } catch {
    /* storage indisponível (modo privado): o banco continua a fonte */
  }
}

export function ClientProvider({ children }) {
  // O contexto de cliente é dependente da SESSÃO: sem usuário autenticado
  // não existe membership para carregar. O ClientProvider vive DENTRO do
  // AuthProvider exatamente por isso.
  const { user } = useAuth();
  const userId = user?.id || null;

  const [clients, setClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState(null);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contextVersion, setContextVersion] = useState(0);
  // true quando a seleção inicial já foi resolvida (banco/local/regra).
  // As rotas autenticadas só renderizam depois disso — evita que um módulo
  // consulte sem o filtro global (corrida na primeira carga).
  const [contextReady, setContextReady] = useState(false);

  // Evita reagir a eventos durante a carga inicial.
  const hydratedRef = useRef(false);

  const applySelection = useCallback(
    (ids, { persistRemote = true, all = false } = {}) => {
      const list = Array.isArray(ids) ? ids : [];
      setSelectedClientIds(list);
      setActiveClientId(list[0] || null);
      setActiveClientContext(userId, list);
      // Libera as requisições que aguardavam o contexto (evita corrida na
      // primeira carga dos módulos).
      markClientContextReady();
      setContextReady(true);
      setContextVersion((v) => v + 1);

      if (!userId) return;
      writeLocal(userId, all ? ALL_CLIENTS_FLAG : list);

      if (persistRemote) {
        api
          .put("/me/client-context", { selected_client_ids: list })
          .catch(() => {
            /* a preferência local permanece; a próxima ação tenta de novo */
          });
      }
    },
    [userId]
  );

  const loadClients = useCallback(async () => {
    if (!userId) {
      setClients([]);
      setActiveClientId(null);
      setSelectedClientIds([]);
      clearActiveClientContext();
      // Sem sessão não há contexto a resolver: libera quem aguardava.
      markClientContextReady();
      setContextReady(true);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [clientsResponse, contextResponse] = await Promise.all([
        api.get("/clients", { skipClientContextWait: true }),
        api
          .get("/me/client-context", { skipClientContextWait: true })
          .catch(() => ({ data: null })),
      ]);

      const list = Array.isArray(clientsResponse.data)
        ? clientsResponse.data
        : clientsResponse.data?.items || [];
      setClients(list);

      if (list.length === 0) {
        applySelection([], { persistRemote: false });
        hydratedRef.current = true;
        return;
      }

      const membershipIds = list.map((c) => c.id);
      const remote = contextResponse.data || {};

      // 1) Preferência do BANCO (fonte de verdade).
      let initial = [];
      if (remote.has_context) {
        initial = (remote.selected_client_ids || []).filter((id) =>
          membershipIds.includes(id)
        );
      } else {
        // 2) Cache local do PRÓPRIO usuário.
        const local = readLocal(userId);
        if (local === ALL_CLIENTS_FLAG) {
          initial = membershipIds;
        } else if (Array.isArray(local)) {
          initial = local.filter((id) => membershipIds.includes(id));
        }
      }

      // 3) Regra explícita e visível: sem preferência salva, o usuário entra
      //    com TODOS os clientes autorizados selecionados (o seletor mostra
      //    "Todos os clientes"). Nunca uma seleção VAZIA — que significaria
      //    "nenhum dado" — nem um recorte silencioso que esconderia o que o
      //    usuário já podia ver. O usuário pode restringir quando quiser.
      if (initial.length === 0) {
        initial = [...membershipIds];
      }

      applySelection(initial, {
        persistRemote: !remote.has_context,
        all: initial.length === membershipIds.length,
      });
      hydratedRef.current = true;
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao carregar clientes");
    } finally {
      setLoading(false);
      // Liberação garantida: mesmo com erro de rede as consultas seguem
      // (com o comportamento anterior, sem filtro), nunca presas.
      markClientContextReady();
      setContextReady(true);
    }
  }, [userId, applySelection]);

  // Troca de usuário (login/logout): o contexto precisa ser resolvido de
  // novo antes das consultas — a preferência de uma conta nunca vale para
  // outra. Só em TROCA real, para não travar recargas normais.
  const prevUserIdRef = useRef(userId);
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      resetClientContextReady();
      setContextReady(false);
    }
  }, [userId]);

  // Carrega o membership quando a sessão existe; limpa o estado no logout.
  // Nunca busca /clients sem usuário autenticado (evita 401 → lista vazia).
  useEffect(() => {
    if (userId) {
      loadClients();
    } else {
      hydratedRef.current = false;
      setClients([]);
      setActiveClientId(null);
      setSelectedClientIds([]);
      setLoading(false);
      setError(null);
      clearActiveClientContext();
    }
  }, [userId, loadClients]);

  // Membership dinâmico: se a autorização efetiva mudar (admin alterou
  // membership, módulos etc.), recarrega a lista de clientes autorizados.
  useEffect(() => {
    if (!userId) return undefined;
    const onAuthChanged = () => {
      loadClients();
    };
    window.addEventListener("astral:authorization-changed", onAuthChanged);
    return () =>
      window.removeEventListener(
        "astral:authorization-changed",
        onAuthChanged
      );
  }, [userId, loadClients]);

  // Sincronização ENTRE ABAS: a preferência é do usuário, não da aba.
  useEffect(() => {
    if (!userId) return undefined;
    const key = storageKeyFor(userId);
    const onStorage = (event) => {
      if (event.key !== key || !hydratedRef.current) return;
      let next = [];
      try {
        const parsed = JSON.parse(event.newValue || "null");
        if (parsed === ALL_CLIENTS_FLAG) {
          next = clients.map((c) => c.id);
        } else if (Array.isArray(parsed)) {
          next = parsed.filter((id) => clients.some((c) => c.id === id));
        }
      } catch {
        return;
      }
      setSelectedClientIds(next);
      setActiveClientId(next[0] || null);
      setActiveClientContext(userId, next);
      setContextVersion((v) => v + 1);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [userId, clients]);

  // Aviso (mesma aba) para quem quiser reagir à mudança de contexto.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(CONTEXT_EVENT, { detail: { selectedClientIds } })
    );
  }, [selectedClientIds]);

  const activeClient = useMemo(
    () => clients.find((c) => c.id === activeClientId) || null,
    [clients, activeClientId]
  );

  const setSelectedClient = useCallback(
    (clientId) => {
      if (!clients.find((c) => c.id === clientId)) return;
      applySelection([clientId]);
    },
    [clients, applySelection]
  );

  const toggleClient = useCallback(
    (clientId) => {
      if (!clients.find((c) => c.id === clientId)) return;
      setSelectedClientIds((prev) => {
        let next;
        if (prev.includes(clientId)) {
          next = prev.filter((id) => id !== clientId);
        } else {
          next = [...prev, clientId];
        }
        setActiveClientId(next[0] || null);
        setActiveClientContext(userId, next);
        setContextVersion((v) => v + 1);
        writeLocal(userId, next);
        api
          .put("/me/client-context", { selected_client_ids: next })
          .catch(() => {});
        return next;
      });
    },
    [clients, userId]
  );

  const selectAll = useCallback(() => {
    applySelection(clients.map((c) => c.id), { all: true });
  }, [clients, applySelection]);

  const selectNone = useCallback(() => {
    applySelection([]);
  }, [applySelection]);

  const setMultiClient = useCallback(
    (clientIds) => {
      const validIds = clientIds.filter((id) =>
        clients.find((c) => c.id === id)
      );
      if (validIds.length === 0) return;
      applySelection(validIds);
    },
    [clients, applySelection]
  );

  const clearMultiClient = useCallback(() => {
    if (activeClientId) applySelection([activeClientId]);
  }, [activeClientId, applySelection]);

  /**
   * Restaura uma seleção VÁLIDA: recarrega os clientes autorizados e
   * reaplica a preferência descartando o que saiu do escopo (ex.: o acesso a
   * uma empresa foi revogado pela administração). Nunca amplia o acesso.
   */
  const restoreValidSelection = useCallback(async () => {
    await loadClients();
  }, [loadClients]);

  const isMulti = selectedClientIds.length > 1;
  const isAll = selectedClientIds.length === clients.length && clients.length > 0;

  const value = {
    clients,
    activeClientId,
    activeClient,
    selectedClientIds,
    isMulti,
    isAll,
    loading,
    error,
    contextVersion,
    contextReady,
    loadClients,
    setSelectedClient,
    toggleClient,
    selectAll,
    selectNone,
    setMultiClient,
    clearMultiClient,
    restoreValidSelection,
    hasClient: (id) => clients.some((c) => c.id === id),
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
}

export function useClient() {
  const ctx = useContext(ClientContext);
  if (!ctx) {
    throw new Error("useClient must be used within ClientProvider");
  }
  return ctx;
}

/** Assina mudanças do contexto global de clientes (mesma aba). */
export function onClientContextChanged(handler) {
  const listener = (event) => handler(event.detail);
  window.addEventListener(CONTEXT_EVENT, listener);
  return () => window.removeEventListener(CONTEXT_EVENT, listener);
}

export default ClientContext;

